import React, { useState } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from "chart.js";
import { Box, Card, CardContent, Typography, Grid, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import MDButton from "components/MDButton";

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

const CommonKpiCharts = ({ kpiData, perspectiveData }) => {
  const { t } = useTranslation();
  const [viewMode, setViewMode] = useState("kpi");

  const toggleView = () => {
    setViewMode((prev) => (prev === "kpi" ? "perspective" : "kpi"));
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6" color="text.primary">
          {viewMode === "kpi" ? t("user.myTeam.kpiView") : t("user.myTeam.perspectiveView")}
        </Typography>
        <MDButton onClick={toggleView} variant="outlined" color="primary">
          {viewMode === "kpi" ? t("user.myTeam.viewByPerspective") : t("user.myTeam.viewByKpi")}
        </MDButton>
      </Stack>

      <Grid container spacing={3}>
        {viewMode === "kpi"
          ? Object.entries(kpiData).map(([kpiName, employees]) => (
              <Grid item xs={12} sm={6} md={4} key={kpiName}>
                <Card sx={{ height: "100%", p: 2 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {kpiName}
                    </Typography>
                    <Box sx={{ height: 250, position: "relative" }}>
                      <Bar
                        key={`kpi-${kpiName}-${viewMode}`}
                        data={{
                          labels: employees.map((e) =>
                            e.name.length > 20 ? e.name.slice(0, 17) + "..." : e.name
                          ),
                          datasets: [
                            {
                              label: t("user.myTeam.achievement"),
                              data: employees.map((e) => e.achievement),
                              backgroundColor: employees.map((e) => {
                                const values = employees.map((emp) => emp.achievement);
                                const max = Math.max(...values);
                                const min = Math.min(...values);
                                if (e.achievement === max) return "rgba(34, 197, 94, 0.7)";
                                if (e.achievement === min) return "rgba(239, 68, 68, 0.7)";
                                return "rgba(107, 114, 128, 0.5)";
                              }),
                              borderRadius: 8,
                              barThickness: 30,
                            },
                          ],
                        }}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: {
                            legend: { display: false },
                            tooltip: {
                              callbacks: {
                                title: (tooltipItems) => {
                                  const index = tooltipItems[0].dataIndex;
                                  return employees[index].name; // full name as tooltip title
                                },
                              },
                            },
                          },
                          scales: {
                            y: { beginAtZero: true, grid: { display: false } },
                            x: { grid: { display: false } },
                          },
                        }}
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))
          : Object.entries(perspectiveData).map(([perspective, kpis]) => {
              const averages = Object.entries(kpis).map(([kpiName, scores]) => ({
                kpiName,
                average: scores.reduce((a, b) => a + b, 0) / scores.length,
                count: scores.length,
              }));

              const maxAvg = Math.max(...averages.map((a) => a.average));
              const minAvg = Math.min(...averages.map((a) => a.average));

              return (
                <Grid item xs={12} md={6} key={perspective}>
                  <Card sx={{ height: "100%", p: 2 }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        {t(`user.assess.perspectives.${perspective}`)}
                      </Typography>
                      <Box sx={{ height: 250, position: "relative" }}>
                        <Bar
                          key={`perspective-${perspective}-${viewMode}`}
                          data={{
                            labels: averages.map((a) =>
                              a.kpiName.length > 25 ? a.kpiName.slice(0, 22) + "..." : a.kpiName
                            ),
                            datasets: [
                              {
                                label: t("user.myTeam.averageScore"),
                                data: averages.map((a) => a.average),
                                backgroundColor: averages.map((a) => {
                                  if (a.average === maxAvg) return "rgba(34, 197, 94, 0.7)";
                                  if (a.average === minAvg) return "rgba(239, 68, 68, 0.7)";
                                  return "rgba(107, 114, 128, 0.5)";
                                }),
                                barThickness: 30,
                                borderRadius: 8,
                              },
                            ],
                          }}
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: {
                              legend: { display: false },
                              tooltip: {
                                callbacks: {
                                  title: (tooltipItems) => {
                                    const index = tooltipItems[0].dataIndex;
                                    return averages[index].kpiName; // Full KPI name
                                  },
                                  label: (context) => {
                                    const avg = context.raw.toFixed(2);
                                    const kpi = context.label;
                                    const employeeCount = averages.find(
                                      (a) => a.kpiName.startsWith(kpi.slice(0, 10)) // fallback match
                                    )?.count;
                                    return `${t(
                                      "user.myTeam.averageScore"
                                    )}: ${avg} (${employeeCount} ${t(
                                      "admin.dashboard.employees"
                                    )})`;
                                  },
                                },
                              },
                            },
                            scales: {
                              y: { beginAtZero: true, grid: { display: false } },
                              x: {
                                grid: { display: false },
                                ticks: {
                                  callback: function (val, index) {
                                    const label = this.getLabelForValue(val);
                                    return label.length > 25 ? label.slice(0, 22) + "..." : label;
                                  },
                                },
                              },
                            },
                          }}
                        />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
      </Grid>
    </Box>
  );
};

CommonKpiCharts.propTypes = {
  kpiData: PropTypes.object.isRequired,
  perspectiveData: PropTypes.object.isRequired,
};

export default CommonKpiCharts;
